import { ref, computed, type ComputedRef } from 'vue';
import { formatDateForApi, isValidDate, parseCivilDate, toNumberOrUndefined } from '@/shared/lib';
import type { EditHomologationFormData } from './editHomologation.types';

const MAX_TRAINING_SESSIONS = 5;

const trainingDateToCivilString = (date: Date | null | undefined): string => {
  if (!date || !isValidDate(date)) return '';
  return formatDateForApi(date) ?? '';
};

const civilStringToTrainingDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const [datePart] = value.split('T');
  return parseCivilDate(datePart);
};

interface TrainingSessionDate {
  date: Date | null;
}

interface TrainingSessionsState {
  sessions: ComputedRef<TrainingSessionDate[]>;
  addSession: () => void;
  removeSession: (index: number) => void;
  canAddMore: ComputedRef<boolean>;
  maxSessions: number;
  getSessionUserId: (index: number) => number | string;
  setSessionUserId: (index: number, value: number | string) => void;
  getSessionPositionId: (index: number) => number | string;
  setSessionPositionId: (index: number, value: number | string) => void;
  getSessionDate: (index: number) => Date | null;
  setSessionDate: (index: number, date: Date | null) => void;
  getSessionDatePickerOpen: (index: number) => boolean;
  setSessionDatePickerOpen: (index: number, open: boolean) => void;
}

export function useTrainingSessions(
  formData: EditHomologationFormData
): TrainingSessionsState {
  const sessionDatePickers = ref<Record<number, boolean>>({});
  const sessions = computed(() => {
    if (!formData.trainingSessionUpdates) return [];
    return formData.trainingSessionUpdates.map((session) => ({
      date: civilStringToTrainingDate(session.trainingDate),
    }));
  });

  const canAddMore = computed(() => {
    const count = formData.trainingSessionUpdates?.length ?? 0;
    return count < MAX_TRAINING_SESSIONS;
  });

  const addSession = (): void => {
    formData.trainingSessionUpdates ??= [];
    if (formData.trainingSessionUpdates.length < MAX_TRAINING_SESSIONS) {
      formData.trainingSessionUpdates.push({
        sessionId: 0,
        trainingId: 0,
        trainingDate: '',
      });
    }
  };

  const removeSession = (index: number): void => {
    if (formData.trainingSessionUpdates && formData.trainingSessionUpdates.length > 1) {
      formData.trainingSessionUpdates.splice(index, 1);
    }
  };

  const getSessionUserId = (index: number): number | string =>
    formData.trainingSessionUpdates?.[index]?.responsibleMemberId ?? '';

  const setSessionUserId = (index: number, value: number | string): void => {
    if (!formData.trainingSessionUpdates?.[index]) return;
    formData.trainingSessionUpdates[index].responsibleMemberId = toNumberOrUndefined(value);
  };

  const getSessionPositionId = (index: number): number | string => {
    return formData.trainingSessionUpdates?.[index]?.responsibleUserPositionId ?? '';
  };

  const setSessionPositionId = (index: number, value: number | string): void => {
    if (formData.trainingSessionUpdates?.[index]) {
      formData.trainingSessionUpdates[index].responsibleUserPositionId = toNumberOrUndefined(value);
    }
  };

  const getSessionDate = (index: number): Date | null => {
    return civilStringToTrainingDate(formData.trainingSessionUpdates?.[index]?.trainingDate);
  };

  const setSessionDate = (index: number, date: Date | null): void => {
    if (formData.trainingSessionUpdates?.[index]) {
      formData.trainingSessionUpdates[index].trainingDate = trainingDateToCivilString(date);
    }
  };

  const getSessionDatePickerOpen = (index: number): boolean => {
    return sessionDatePickers.value[index] ?? false;
  };

  const setSessionDatePickerOpen = (index: number, open: boolean): void => {
    sessionDatePickers.value[index] = open;
  };

  return {
    sessions,
    addSession,
    removeSession,
    canAddMore,
    maxSessions: MAX_TRAINING_SESSIONS,
    getSessionUserId,
    setSessionUserId,
    getSessionPositionId,
    setSessionPositionId,
    getSessionDate,
    setSessionDate,
    getSessionDatePickerOpen,
    setSessionDatePickerOpen,
  };
}
